import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography,
  notification,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  ReloadOutlined,
  SwapOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import usePlanRequests from '../hooks/usePlanRequests';
import PlanRemovalBlockedModal from './PlanRemovalBlockedModal';
import OrganizationAutocomplete from './OrganizationAutocomplete';
import {
  PLAN_REQUEST_BOARD_TABS,
  PLAN_REQUEST_STATUSES,
  PLAN_REMOVAL_STATUSES,
  buildAdminOfferPayload,
  canApprovePlanRequest,
  canCounterPlanRequest,
  canForceApprovePlanRequest,
  canRejectPlanRequest,
  formatOfferActor,
  formatPlanRequestCost,
  formatWaitingOn,
  getNegotiationTerms,
  getPlanRequestOffers,
  isAcceptRequired,
  isNegotiationFlow,
  isPlanRemovalBlocked,
  isPlanRequestOpen,
  isRemovePlanRequest,
  offerFamilyCost,
  offerFamilyHeads,
  offerIndividualHeads,
  offerUnitCost,
  orgDetailPathFromRequest,
  planRequestActionColor,
  planRequestStatusColor,
  supportsFamilyTier,
  supportsIndividualTier,
} from '../planRequestHelpers';
import {
  formatPlanMoney,
  formatPlanReferencePrices,
} from '../../plans/planCatalogHelpers';
import PlanPriceLevelHint from '../../plans/PlanPriceLevelHint';

const { Text, Title } = Typography;
const { TextArea } = Input;

const moneyInputProps = {
  style: { width: '100%' },
  min: 0,
  placeholder: 'Unit cost',
  formatter: (v) => (v == null || v === '' ? '' : `₦ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')),
  parser: (v) => (v ? v.replace(/[₦,\s]/g, '') : ''),
};

function PlanRequestCostCompare({ row, variant = 'table' }) {
  if (!row || !isNegotiationFlow(row)) return variant === 'table' ? '—' : null;
  const ref = formatPlanReferencePrices(row.reference_prices);
  const terms = getNegotiationTerms(row);
  const requestedUnit = formatPlanRequestCost(terms.individual_unit);
  const requestedFamily = formatPlanRequestCost(terms.family_unit);
  const refUnit = ref ? formatPlanMoney(ref.corporate_unit) : '—';
  const refFamily = ref ? formatPlanMoney(ref.corporate_family) : '—';

  if (variant === 'modal' || variant === 'drawer') {
    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Corporate list (<PlanPriceLevelHint level="L2">L2</PlanPriceLevelHint>): {refUnit} unit · {refFamily} family
        </Text>
        <Text style={{ fontSize: 13 }}>
          Latest terms (<PlanPriceLevelHint level="L3">L3</PlanPriceLevelHint>): {requestedUnit} unit
          {terms.individual_heads != null ? ` × ${terms.individual_heads} policies` : ''}
          {' · '}
          {requestedFamily} family
          {terms.family_heads != null ? ` × ${terms.family_heads} policies` : ''}
        </Text>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={2}>
      <Text type="secondary" style={{ fontSize: 11 }}>
        <PlanPriceLevelHint level="L2">L2</PlanPriceLevelHint> {refUnit} / {refFamily}
      </Text>
      <Text style={{ fontSize: 12 }}>
        Offer {requestedUnit}
        {terms.individual_heads != null ? ` (${terms.individual_heads})` : ''}
        {' / '}
        {requestedFamily}
        {terms.family_heads != null ? ` (${terms.family_heads})` : ''}
      </Text>
    </Space>
  );
}

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Care-admin plan negotiation / removal ticket board.
 * Separate tabs: Negotiations (action=add) vs Removals (action=remove).
 */
const PlanRequestsPanel = ({
  scopedOrgId = null,
  orgName = '',
  showOrgFilter = false,
  filterOrgId = null,
  filterOrgLabel = '',
  onOrgFilterChange,
  embedded = false,
  onPendingCountChange,
}) => {
  const navigate = useNavigate();

  const {
    status,
    setStatus,
    action,
    setAction,
    boardCounts,
    setOrgFilter,
    orgFilterLocked,
    rows,
    meta,
    loading,
    error,
    changePage,
    refetch,
    getRequestDetail,
    approveRequest,
    rejectRequest,
    counterOffer,
    checkRemoval,
  } = usePlanRequests({ initialStatus: 'open', initialAction: 'add', scopedOrgId });

  const [detailRow, setDetailRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [removalCheck, setRemovalCheck] = useState(null);
  const [removalCheckLoading, setRemovalCheckLoading] = useState(false);
  const [actionRow, setActionRow] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [blockedModal, setBlockedModal] = useState({ open: false, policies: [], message: '' });
  const [acceptRequiredOpen, setAcceptRequiredOpen] = useState(false);
  const [reviewForm] = Form.useForm();

  const isNegotiationsBoard = action === 'add';
  const statusOptions = isNegotiationsBoard ? PLAN_REQUEST_STATUSES : PLAN_REMOVAL_STATUSES;

  useEffect(() => {
    if (orgFilterLocked || !showOrgFilter) return;
    setOrgFilter(filterOrgId ?? null);
  }, [filterOrgId, showOrgFilter, orgFilterLocked, setOrgFilter]);

  useEffect(() => {
    if (typeof onPendingCountChange === 'function') {
      onPendingCountChange(boardCounts.total || 0);
    }
  }, [boardCounts.total, onPendingCountChange]);

  const loadRemovalCheck = useCallback(async (row) => {
    if (!row || !isRemovePlanRequest(row)) {
      setRemovalCheck(null);
      return;
    }
    setRemovalCheckLoading(true);
    setRemovalCheck(null);
    try {
      const parsed = await checkRemoval(row.org_social_no, row.plan_id);
      if (parsed.ok) setRemovalCheck(parsed.data ?? null);
      else setRemovalCheck({ can_remove: false, error: parsed.message });
    } finally {
      setRemovalCheckLoading(false);
    }
  }, [checkRemoval]);

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailLoading(true);
    try {
      const parsed = await getRequestDetail(row.id);
      if (parsed.ok) {
        const detail = parsed.data && !Array.isArray(parsed.data) ? parsed.data : row;
        setDetailRow({ ...row, ...detail });
      }
    } finally {
      setDetailLoading(false);
    }
  }, [getRequestDetail]);

  useEffect(() => {
    if (detailRow) loadRemovalCheck(detailRow);
    else setRemovalCheck(null);
  }, [detailRow?.id, detailRow?.action, detailRow?.flow, loadRemovalCheck]); // eslint-disable-line react-hooks/exhaustive-deps

  const seedOfferFields = (row) => {
    if (!row || !isNegotiationFlow(row)) return;
    const terms = getNegotiationTerms(row);
    reviewForm.setFieldsValue({
      org_plan_cost: terms.individual_unit != null ? Number(terms.individual_unit) : null,
      org_plan_family_cost: terms.family_unit != null ? Number(terms.family_unit) : null,
      // Prefill from submitted expected policy counts (`tentative_heads` on the offer).
      individual_heads: terms.individual_heads != null ? Number(terms.individual_heads) : null,
      family_heads: terms.family_heads != null ? Number(terms.family_heads) : null,
      note: '',
    });
  };

  const openReviewModal = async (row, type) => {
    setActionType(type);
    reviewForm.resetFields();
    if (!['approve', 'force', 'counter'].includes(type) || !isNegotiationFlow(row)) {
      setActionRow(row);
      return;
    }

    // Always load detail for negotiation offers — list rows often omit offers[] / expected policies.
    let source = row;
    if (row?.id != null) {
      const parsed = await getRequestDetail(row.id);
      if (parsed.ok && parsed.data && !Array.isArray(parsed.data)) {
        source = { ...row, ...parsed.data };
      }
    }
    setActionRow(source);
    seedOfferFields(source);
  };

  // Re-seed after the modal Form mounts so expected policies aren't lost to reset timing.
  useEffect(() => {
    if (!actionRow || !actionType) return;
    if (!['approve', 'force', 'counter'].includes(actionType) || !isNegotiationFlow(actionRow)) return;
    seedOfferFields(actionRow);
  }, [actionRow, actionType]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeReviewModal = () => {
    setActionRow(null);
    setActionType(null);
    reviewForm.resetFields();
  };

  const showBlocked = (parsed) => {
    setBlockedModal({
      open: true,
      policies: parsed.blockingPolicies ?? [],
      message: parsed.message,
    });
  };

  const modalTitle = (() => {
    switch (actionType) {
      case 'force': return 'Force-approve negotiation';
      case 'counter': return 'Send counter offer';
      case 'reject': return isNegotiationsBoard ? 'Reject negotiation' : 'Reject removal ticket';
      case 'approve': return isNegotiationsBoard ? 'Approve negotiation' : 'Approve removal ticket';
      default: return 'Plan request';
    }
  })();

  const submitReview = async ({ force = false } = {}) => {
    if (!actionRow || !actionType) return;
    const values = await reviewForm.validateFields().catch(() => null);
    if (!values && actionType !== 'force') return;
    const formValues = values || reviewForm.getFieldsValue(true);

    if (actionType === 'approve' && isRemovePlanRequest(actionRow)) {
      const check = removalCheck
        ?? (await checkRemoval(actionRow.org_social_no, actionRow.plan_id).then((p) => p.data));
      if (check && check.can_remove === false) {
        showBlocked({
          blockingPolicies: check.blocking_policies ?? [],
          message: 'Removal is blocked for this plan. Active members have care history.',
        });
        return;
      }
    }

    setActionLoading(true);
    try {
      const useForce = force || actionType === 'force';
      let parsed;

      if (actionType === 'counter') {
        parsed = await counterOffer(actionRow.id, buildAdminOfferPayload(formValues, actionRow));
      } else if (actionType === 'reject') {
        parsed = await rejectRequest(actionRow.id, formValues.note);
      } else {
        const offerPayload = isNegotiationFlow(actionRow)
          ? buildAdminOfferPayload(formValues, actionRow)
          : {};
        parsed = await approveRequest(actionRow.id, {
          note: formValues.note,
          force: useForce,
          org_plan_cost: offerPayload.org_plan_cost ?? null,
          org_plan_family_cost: offerPayload.org_plan_family_cost ?? null,
        });
      }

      if ((actionType === 'approve' || actionType === 'force' || useForce) && isPlanRemovalBlocked(parsed)) {
        showBlocked(parsed);
        return;
      }

      if ((actionType === 'approve' || actionType === 'force') && isAcceptRequired(parsed)) {
        setAcceptRequiredOpen(true);
        return;
      }

      if (parsed.ok) {
        setAcceptRequiredOpen(false);
        closeReviewModal();
        setDetailRow(null);
      } else if (actionType !== 'counter' && actionType !== 'reject') {
        notification.error({ message: parsed.message || 'Action failed', placement: 'topRight' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const hideOrgColumn = orgFilterLocked || Boolean(filterOrgId);

  const renderRowActions = (row) => {
    const open = isPlanRequestOpen(row);
    return (
      <Space size="small" wrap>
        {open && canApprovePlanRequest(row) ? (
          <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => openReviewModal(row, 'approve')}>
            Approve
          </Button>
        ) : null}
        {open && canCounterPlanRequest(row) ? (
          <Button size="small" icon={<SwapOutlined />} onClick={() => openReviewModal(row, 'counter')}>
            Counter
          </Button>
        ) : null}
        {open && canForceApprovePlanRequest(row) ? (
          <Button size="small" icon={<ThunderboltOutlined />} onClick={() => openReviewModal(row, 'force')}>
            Approve
          </Button>
        ) : null}
        {open && canRejectPlanRequest(row) ? (
          <Button size="small" danger icon={<CloseOutlined />} onClick={() => openReviewModal(row, 'reject')}>
            Reject
          </Button>
        ) : null}
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(row)}>
          Details
        </Button>
      </Space>
    );
  };

  const columns = useMemo(() => {
    const cols = [];
    if (!hideOrgColumn) {
      cols.push({
        title: 'Organization',
        key: 'org',
        render: (_, row) => (
          <div>
            <Text strong>{row.org_name || '—'}</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[row.org_code, row.org_social_no].filter(Boolean).join(' · ')}
              </Text>
            </div>
          </div>
        ),
      });
    }

    cols.push(
      {
        title: 'Plan',
        key: 'plan',
        render: (_, row) => (
          <div>
            <Text>{row.plan_name || '—'}</Text>
            {row.plan_id != null ? (
              <div><Text type="secondary" style={{ fontSize: 12 }}>ID {row.plan_id}</Text></div>
            ) : null}
          </div>
        ),
      },
    );

    if (isNegotiationsBoard) {
      cols.push(
        {
          title: (
            <span>
              Terms / <PlanPriceLevelHint level="L2">L2</PlanPriceLevelHint>
            </span>
          ),
          key: 'cost',
          width: 170,
          render: (_, row) => <PlanRequestCostCompare row={row} />,
        },
        {
          title: 'Negotiation',
          key: 'negotiation',
          width: 150,
          render: (_, row) => {
            const waiting = formatWaitingOn(row.waiting_on || row.negotiation?.waiting_on);
            const offers = Number(row.offer_count ?? row.negotiation?.offer_count) || 0;
            const actor = row.latest_offer_actor || row.negotiation?.latest_offer_actor;
            return (
              <Space direction="vertical" size={0}>
                {waiting ? <Text style={{ fontSize: 12 }}>{waiting}</Text> : null}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {offers} offer{offers === 1 ? '' : 's'}
                  {actor ? ` · ${formatOfferActor({ actor_type: actor })}` : ''}
                </Text>
              </Space>
            );
          },
        },
      );
    } else {
      cols.push({
        title: 'Reason',
        key: 'reason',
        ellipsis: true,
        render: (_, row) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.review_note || row.request_note || row.note || '—'}
          </Text>
        ),
      });
    }

    cols.push(
      {
        title: 'Requested',
        key: 'requested',
        width: 140,
        render: (_, row) => (
          <div>
            <Text style={{ fontSize: 12 }}>{formatDateTime(row.created_at)}</Text>
            {row.requested_by_type ? (
              <div><Text type="secondary" style={{ fontSize: 11 }}>{row.requested_by_type}</Text></div>
            ) : null}
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (s) => (
          <Tag color={planRequestStatusColor(s)} style={{ margin: 0 }}>
            {(s || '—').replace(/_/g, ' ')}
          </Tag>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: isNegotiationsBoard ? 300 : 220,
        render: (_, row) => renderRowActions(row),
      },
    );

    return cols;
  }, [hideOrgColumn, isNegotiationsBoard]); // eslint-disable-line react-hooks/exhaustive-deps

  const removalBlocked = isRemovePlanRequest(detailRow)
    && removalCheck
    && removalCheck.can_remove === false;

  const emptyText = isNegotiationsBoard
    ? (status === 'open' ? 'No open negotiations' : 'No negotiations in this status')
    : (status === 'open' ? 'No open removal tickets' : 'No removal tickets in this status');

  const handleOrgSelect = (org) => {
    if (typeof onOrgFilterChange === 'function') onOrgFilterChange(org);
    else setOrgFilter(org?.org_id ?? null);
  };

  const clearOrgFilter = () => {
    if (typeof onOrgFilterChange === 'function') onOrgFilterChange(null);
    else setOrgFilter(null);
  };

  const offers = getPlanRequestOffers(detailRow);
  const detailOpen = isPlanRequestOpen(detailRow);

  const boardTabOptions = PLAN_REQUEST_BOARD_TABS.map((tab) => {
    const count = tab.value === 'add' ? boardCounts.add : boardCounts.remove;
    return {
      value: tab.value,
      label: count > 0 ? (
        <span className="dash-tab-label dash-tab-label--attention">
          <span>{tab.label}</span>
          <Badge count={count} size="small" overflowCount={99} className="dash-tab-badge" />
        </span>
      ) : tab.label,
    };
  });

  return (
    <div className={embedded ? 'plan-requests-panel plan-requests-panel--embedded' : 'plan-requests-panel'}>
      {embedded && orgName ? (
        <div className="org-detail-context-strip" style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Negotiations and removal tickets for <Text strong>{orgName}</Text>.
            Adds use offer rounds; removals are approve/reject only.
          </Text>
        </div>
      ) : (
        <Alert
          type="info"
          showIcon
          message="Plan request board"
          description="Negotiations (add) use counter → org accept → approve, with force-approve as an ops escape hatch. Removals are ticket-style approve/reject with no offer ladder."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="org-detail-toolbar" style={{ marginBottom: 12 }}>
        <Space wrap size="middle" style={{ flex: 1 }}>
          <Segmented value={action} onChange={setAction} options={boardTabOptions} />
          {showOrgFilter ? (
            <div style={{ minWidth: 240, maxWidth: 320 }}>
              <OrganizationAutocomplete placeholder="Filter by organization…" onSelect={handleOrgSelect} />
            </div>
          ) : null}
          {showOrgFilter && filterOrgId ? (
            <Tag closable onClose={clearOrgFilter} style={{ margin: 0, padding: '4px 8px' }}>
              {filterOrgLabel || `Org #${filterOrgId}`}
            </Tag>
          ) : null}
        </Space>
        <Button size="small" icon={<ReloadOutlined spin={loading} />} onClick={refetch} loading={loading}>
          Refresh
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={status}
          onChange={setStatus}
          options={statusOptions.map((s) => ({ label: s.label, value: s.value }))}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Could not load plan requests"
          description={error.includes('missing') || error.toLowerCase().includes('migration')
            ? `${error} — ensure plan-request + negotiation/offers migrations have been applied.`
            : error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Table
        className={embedded ? 'org-policies-table' : undefined}
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        size={embedded ? 'small' : 'middle'}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: true,
          showTotal: (total) => `${total} ${isNegotiationsBoard ? 'negotiation' : 'ticket'}${total === 1 ? '' : 's'}`,
          onChange: (page, pageSize) => changePage(page, pageSize),
        }}
        locale={{ emptyText }}
      />

      <Drawer
        title={isRemovePlanRequest(detailRow) ? 'Removal ticket' : 'Negotiation detail'}
        width={540}
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        extra={detailRow && detailOpen ? (
          <Space wrap>
            {canApprovePlanRequest(detailRow) ? (
              <Button type="primary" size="small" disabled={removalBlocked} onClick={() => openReviewModal(detailRow, 'approve')}>
                Approve
              </Button>
            ) : null}
            {canCounterPlanRequest(detailRow) ? (
              <Button size="small" icon={<SwapOutlined />} onClick={() => openReviewModal(detailRow, 'counter')}>
                Counter
              </Button>
            ) : null}
            {canForceApprovePlanRequest(detailRow) ? (
              <Button size="small" icon={<ThunderboltOutlined />} onClick={() => openReviewModal(detailRow, 'force')}>
                Approve
              </Button>
            ) : null}
            {canRejectPlanRequest(detailRow) ? (
              <Button size="small" danger onClick={() => openReviewModal(detailRow, 'reject')}>
                Reject
              </Button>
            ) : null}
          </Space>
        ) : null}
      >
        {detailLoading && !detailRow ? <Spin /> : null}
        {detailRow ? (
          <>
            <Descriptions column={1} size="small" bordered>
              {!hideOrgColumn ? (
                <>
                  <Descriptions.Item label="Organization">{detailRow.org_name}</Descriptions.Item>
                  <Descriptions.Item label="Org code">{detailRow.org_social_no}</Descriptions.Item>
                </>
              ) : null}
              <Descriptions.Item label="Flow">
                <Tag color={planRequestActionColor(detailRow.action)}>
                  {detailRow.flow || (isRemovePlanRequest(detailRow) ? 'removal_ticket' : 'negotiation')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Plan">{detailRow.plan_name} (ID {detailRow.plan_id})</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={planRequestStatusColor(detailRow.status)}>
                  {(detailRow.status || '—').replace(/_/g, ' ')}
                </Tag>
              </Descriptions.Item>
              {formatWaitingOn(detailRow.waiting_on) ? (
                <Descriptions.Item label="Waiting on">{formatWaitingOn(detailRow.waiting_on)}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Requested">{formatDateTime(detailRow.created_at)}</Descriptions.Item>
              {(detailRow.review_note || detailRow.request_note) ? (
                <Descriptions.Item label={isRemovePlanRequest(detailRow) ? 'Reason' : 'Note'}>
                  {detailRow.review_note || detailRow.request_note}
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            {isNegotiationFlow(detailRow) ? (
              <div style={{ marginTop: 16 }}>
                <Title level={5} style={{ marginBottom: 8 }}>Latest terms</Title>
                <PlanRequestCostCompare row={detailRow} variant="drawer" />
              </div>
            ) : null}

            {isNegotiationFlow(detailRow) && offers.length > 0 ? (
              <div style={{ marginTop: 24 }}>
                <Title level={5}>Offer ladder</Title>
                <Timeline
                  items={[...offers]
                    .sort((a, b) => (Number(a.round) || 0) - (Number(b.round) || 0))
                    .map((offer, idx) => ({
                      key: offer.id || idx,
                      children: (
                        <div>
                          <Text strong>
                            Round {offer.round ?? idx + 1}
                            {' · '}
                            {formatOfferActor(offer)}
                          </Text>
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            {formatDateTime(offer.created_at)}
                          </Text>
                          <div style={{ fontSize: 13 }}>
                            Individual {formatPlanRequestCost(offerUnitCost(offer))}
                            {offerIndividualHeads(offer) != null ? ` × ${offerIndividualHeads(offer)} policies` : ''}
                            {' · '}
                            Family {formatPlanRequestCost(offerFamilyCost(offer))}
                            {offerFamilyHeads(offer) != null ? ` × ${offerFamilyHeads(offer)} policies` : ''}
                          </div>
                          {offer.note ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>{offer.note}</Text>
                          ) : null}
                        </div>
                      ),
                    }))}
                />
              </div>
            ) : null}

            {!hideOrgColumn && orgDetailPathFromRequest(detailRow) ? (
              <Button
                type="link"
                style={{ marginTop: 16, paddingLeft: 0 }}
                onClick={() => navigate(orgDetailPathFromRequest(detailRow), {
                  state: {
                    org_id: detailRow.organization_id,
                    org_name: detailRow.org_name,
                    org_social_no: detailRow.org_social_no,
                  },
                })}
              >
                View organization
              </Button>
            ) : null}

            {isRemovePlanRequest(detailRow) ? (
              <div style={{ marginTop: 24 }}>
                <Title level={5}>Removal check</Title>
                {removalCheckLoading ? <Spin size="small" /> : null}
                {!removalCheckLoading && removalCheck?.can_remove === true ? (
                  <Alert type="success" showIcon message="Eligible for removal — no blocking active policies detected." />
                ) : null}
                {!removalCheckLoading && removalBlocked ? (
                  <>
                    <Alert
                      type="warning"
                      showIcon
                      message="Removal blocked"
                      description="Active members on this plan have care history."
                      style={{ marginBottom: 12 }}
                    />
                    <Button
                      size="small"
                      onClick={() => showBlocked({
                        blockingPolicies: removalCheck.blocking_policies ?? [],
                        message: 'Active members have care history on this plan.',
                      })}
                    >
                      View blocking policies
                    </Button>
                  </>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <Modal
        title={modalTitle}
        open={Boolean(actionRow && actionType)}
        onCancel={closeReviewModal}
        confirmLoading={actionLoading}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={closeReviewModal} disabled={actionLoading}>Cancel</Button>,
          <Button
            key="submit"
            type="primary"
            danger={actionType === 'reject'}
            onClick={() => submitReview()}
            loading={actionLoading}
          >
            {actionType === 'force' ? 'Force approve'
              : actionType === 'counter' ? 'Send counter'
                : actionType === 'reject' ? 'Reject'
                  : 'Approve'}
          </Button>,
        ]}
      >
        {actionRow ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text>
              {!hideOrgColumn && actionRow.org_name ? <><Text strong>{actionRow.org_name}</Text>{' — '}</> : null}
              <Tag color={planRequestActionColor(actionRow.action)}>{actionRow.action}</Tag>
              {' '}
              <Text strong>{actionRow.plan_name}</Text>
              {' '}
              <Tag color={planRequestStatusColor(actionRow.status)}>
                {(actionRow.status || '').replace(/_/g, ' ')}
              </Tag>
            </Text>

            {actionType === 'force' ? (
              <Alert
                type="warning"
                showIcon
                message="Force approve skips HR acceptance"
                description="Use only with a signed agreement / ops exception."
              />
            ) : null}

            {actionType === 'counter' ? (
              <Alert
                type="info"
                showIcon
                message="Counter offer"
                description="Creates a new account-team round. Organization can accept or counter again."
              />
            ) : null}

            <Form form={reviewForm} layout="vertical">
              {['approve', 'force', 'counter'].includes(actionType) && isNegotiationFlow(actionRow) ? (
                <>
                  <PlanRequestCostCompare row={actionRow} variant="modal" />
                  {supportsIndividualTier(actionRow) ? (
                    <Space size="middle" style={{ width: '100%', marginTop: 8 }} align="start">
                      <Form.Item name="org_plan_cost" label="Individual unit cost" style={{ flex: 1, marginBottom: 12 }}>
                        <InputNumber {...moneyInputProps} />
                      </Form.Item>
                      <Form.Item name="individual_heads" label="Expected policies" style={{ width: 160, marginBottom: 12 }}>
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="Policies" />
                      </Form.Item>
                    </Space>
                  ) : null}
                  {supportsFamilyTier(actionRow) ? (
                    <Space size="middle" style={{ width: '100%' }} align="start">
                      <Form.Item name="org_plan_family_cost" label="Family unit cost" style={{ flex: 1, marginBottom: 12 }}>
                        <InputNumber {...moneyInputProps} />
                      </Form.Item>
                      <Form.Item name="family_heads" label="Expected policies" style={{ width: 160, marginBottom: 12 }}>
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="Policies" />
                      </Form.Item>
                    </Space>
                  ) : null}
                </>
              ) : null}
              <Form.Item
                name="note"
                label={actionType === 'counter' ? 'Offer note' : 'Review note'}
                rules={actionType === 'reject'
                  ? [{ required: true, message: 'Please provide a reason for rejection' }]
                  : []}
              >
                <TextArea
                  rows={3}
                  placeholder={
                    actionType === 'reject' ? 'Reason for rejection…'
                      : actionType === 'counter' ? 'Optional note to the organization…'
                        : 'Optional note…'
                  }
                />
              </Form.Item>
            </Form>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title="Organization must accept first"
        open={acceptRequiredOpen}
        onCancel={() => setAcceptRequiredOpen(false)}
        footer={[
          <Button key="wait" onClick={() => setAcceptRequiredOpen(false)}>Wait for HR</Button>,
          canForceApprovePlanRequest(actionRow) ? (
            <Button
              key="force"
              type="primary"
              danger
              loading={actionLoading}
              icon={<ThunderboltOutlined />}
              onClick={() => submitReview({ force: true })}
            >
              Force approve anyway
            </Button>
          ) : null,
        ].filter(Boolean)}
      >
        <Alert
          type="warning"
          showIcon
          message="ACCEPT_REQUIRED"
          description="This add request is not org-accepted yet. Wait for HR to accept your offer, or force-approve with a signed agreement."
        />
      </Modal>

      <PlanRemovalBlockedModal
        open={blockedModal.open}
        blockingPolicies={blockedModal.policies}
        message={blockedModal.message}
        onClose={() => setBlockedModal({ open: false, policies: [], message: '' })}
      />
    </div>
  );
};

export default PlanRequestsPanel;
